import { ViewModels } from '../../../../services/backendServices';
import { CloudCaptureDisposition, VFTDisposition } from '../../../../services/ValidationFailureTypes/Api/ViewModels';
import { UnexpectedCase } from '../../../../utils/UnexpectedCase';
import { parseXMLBoolean, parseXMLNumber } from '../../../../utils/xmlValueConverter';
import { ScriptingEngineEnvironment } from '../../../CustomJSRuleEngineV2';
import { FlattenedXrefParameters } from './flattenXrefParameters';

export type ParsedSettingValueType = string | number | boolean | string[] | number[];

export interface InputDefinition {
    InputKey: string;

    Label: string;

    Type: ViewModels.InputParameterSettingType;

    DefaultValue?: ParsedSettingValueType;
}

export interface ParsedInputParameterSetting {
    Name: string;

    Label: string;

    Description: string;

    ErrorCode?: string;

    Type: ViewModels.InputParameterSettingType;

    ConfigurableByCompany: boolean;

    DefaultValue?: ParsedSettingValueType;

    Inputs?: InputDefinition[];

    DocumentIndexes?: ScriptingEngineEnvironment.Touchpoint.Config.DocumentIndex[];

    ConfigurableErrorTexts?: boolean;

    DefaultEnabled?: boolean;
}

export interface ParsedInputParameter {
    Name: string;

    Value: ParsedSettingValueType;

    Disposition: VFTDisposition | null;

    CloudCaptureDisposition: CloudCaptureDisposition | null;

    ValidationClass: NonNullable<ViewModels.InputParameter['ValidationClass']> | null;

    DefaultDisposition?: VFTDisposition | null;

    Enabled: boolean;

    InputValues?: Record<string, ParsedSettingValueType>;

    TitleText: string | null;

    TitleArguments: string | null;

    DetailText: string | null;

    DetailArguments: string | null;
}

export interface ParsedXrefParameters {
    ruleParameters: ParsedInputParameterSetting[];

    xrefParameterValues: ParsedInputParameter[];

    parameterOverrides: {
        id: number | null;
        vendorClasses: string[];
        parameterValues: ParsedInputParameter[];
    }[];
}

type ParamTypeMap = Map<string, ViewModels.InputParameterSettingType>;

const createParamTypeKey = (paramName: string, inputKey?: string) =>
    inputKey ? `${paramName}-${inputKey}` : paramName;

function createParamTypeMap(inputParameterSettings: ViewModels.InputParameterSetting[]) {
    const mapEntries = inputParameterSettings
        .map((inputParameterSetting) => {
            const legacyDefinition = [
                createParamTypeKey(inputParameterSetting.Name),
                inputParameterSetting.Type,
            ] as const;

            if (!inputParameterSetting.Inputs?.Input) {
                return [legacyDefinition];
            }

            const inputDefinitions = Array.isArray(inputParameterSetting.Inputs.Input)
                ? inputParameterSetting.Inputs.Input
                : [inputParameterSetting.Inputs.Input];

            const inputDefinitionsWithKeys = inputDefinitions.map(
                (input) => [createParamTypeKey(inputParameterSetting.Name, input.InputKey), input.Type] as const
            );

            return [legacyDefinition, ...inputDefinitionsWithKeys];
        })
        .flat();

    return new Map<string, ViewModels.InputParameterSettingType>(mapEntries);
}

function parseParamValue(
    paramType: ViewModels.InputParameterSettingType,
    value: string | string[]
): ParsedSettingValueType {
    switch (paramType) {
        case 'Array<number>': {
            if (!Array.isArray(value)) {
                throw new Error('Expected array type parameter value');
            }

            return value.map(parseXMLNumber);
        }
        case 'Array<string>': {
            if (!Array.isArray(value)) {
                throw new Error('Expected array type parameter value');
            }

            return value;
        }
        case 'boolean': {
            if (Array.isArray(value)) {
                throw new Error('Expected primitive type parameter value');
            }

            return parseXMLBoolean(value);
        }
        case 'number': {
            if (Array.isArray(value)) {
                throw new Error('Expected primitive type parameter value');
            }

            return parseXMLNumber(value);
        }
        case 'string': {
            if (Array.isArray(value)) {
                throw new Error('Expected primitive type parameter value');
            }

            return value;
        }
        default:
            throw new UnexpectedCase(paramType);
    }
}

function parseInputParameterSettingDefaultValue(
    paramType: ViewModels.InputParameterSettingType,
    defaultValue: string | string[] | undefined
) {
    if (defaultValue === undefined) {
        return undefined;
    }

    return parseParamValue(paramType, defaultValue);
}

function parseRuleParameter(ruleParameter: ViewModels.InputParameterSetting): ParsedInputParameterSetting {
    const inputs = Array.isArray(ruleParameter.Inputs?.Input)
        ? ruleParameter.Inputs!.Input
        : ruleParameter.Inputs?.Input
        ? [ruleParameter.Inputs.Input]
        : [];

    const parsedInputs = inputs.map((input) => ({
        ...input,
        DefaultValue: parseInputParameterSettingDefaultValue(input.Type, input.DefaultValue),
    }));

    const documentIndexes = Array.isArray(ruleParameter.DocumentIndexes?.DocumentIndex)
        ? ruleParameter.DocumentIndexes!.DocumentIndex
        : ruleParameter.DocumentIndexes?.DocumentIndex
        ? [ruleParameter.DocumentIndexes.DocumentIndex]
        : undefined;

    return {
        ...ruleParameter,
        ConfigurableByCompany: parseXMLBoolean(ruleParameter.ConfigurableByCompany),
        DefaultValue: parseInputParameterSettingDefaultValue(ruleParameter.Type, ruleParameter.DefaultValue),
        Inputs: parsedInputs,
        DocumentIndexes: documentIndexes,
        ConfigurableErrorTexts: ruleParameter.ConfigurableErrorTexts
            ? parseXMLBoolean(ruleParameter.ConfigurableErrorTexts)
            : undefined,
        DefaultEnabled: ruleParameter.DefaultEnabled ? parseXMLBoolean(ruleParameter.DefaultEnabled) : undefined,
    };
}

export function parseRuleParameters(ruleParameters: ViewModels.InputParameterSetting[]): ParsedInputParameterSetting[] {
    return ruleParameters.map(parseRuleParameter);
}

function parseInputParameters(
    paramTypeMap: ParamTypeMap,
    inputParameters: ViewModels.InputParameter[]
): ParsedInputParameter[] {
    return (
        inputParameters
            // it is possible for an InputParameterSetting to be removed from a rule, in which case it won't be in the map
            // in this case we want to filter out such an InputParameter because the InputParameter no longer has any
            // meaning.
            .filter((inputParameter) => paramTypeMap.has(inputParameter.Name))
            .map((inputParameter) => {
                const disposition = Number(inputParameter.Disposition);
                const cloudCaptureDisposition = Number(inputParameter.CloudCaptureDisposition);

                const parsedInputs = Object.entries(inputParameter.InputValues ?? {})
                    .filter(
                        ([inputKey]) =>
                            inputKey !== '@id' && paramTypeMap.has(createParamTypeKey(inputParameter.Name, inputKey))
                    )
                    .map(([inputKey, value]) => [
                        inputKey,
                        parseParamValue(paramTypeMap.get(createParamTypeKey(inputParameter.Name, inputKey))!, value),
                    ]);

                return {
                    Name: inputParameter.Name,
                    // the type assertion is fine since we checked membership in the map above
                    Value: parseParamValue(paramTypeMap.get(inputParameter.Name)!, inputParameter.Value),
                    Enabled: inputParameter.Enabled === 'false' ? false : true,
                    Disposition: Number.isNaN(disposition) ? null : disposition,
                    CloudCaptureDisposition:
                        cloudCaptureDisposition === CloudCaptureDisposition.ReturnToBuyer ||
                        cloudCaptureDisposition === CloudCaptureDisposition.SilentRejection
                            ? cloudCaptureDisposition
                            : null,
                    ValidationClass: inputParameter.ValidationClass ?? null,
                    InputValues: Object.fromEntries(parsedInputs),
                    TitleText: inputParameter.TitleText ?? null,
                    TitleArguments: inputParameter.TitleArguments ?? null,
                    DetailText: inputParameter.DetailText ?? null,
                    DetailArguments: inputParameter.DetailArguments ?? null,
                };
            })
    );
}

/**
 * Parses the InputParameterSetting and InputParameter values to runtime JS values of the correct types.
 *
 * @param xref the flattened xref parameters
 * @returns the parsed xref parameters
 */
export function parseXrefParameters(xref: FlattenedXrefParameters): ParsedXrefParameters {
    const paramTypeMap = createParamTypeMap(xref.ruleParameters);

    return {
        ruleParameters: parseRuleParameters(xref.ruleParameters),
        xrefParameterValues: parseInputParameters(paramTypeMap, xref.xrefParameterValues),
        parameterOverrides: xref.parameterOverrides.map((override) => ({
            id: override.id,
            vendorClasses: override.vendorClasses,
            parameterValues: parseInputParameters(paramTypeMap, override.parameterValues),
        })),
    };
}
